import { Injectable } from '@angular/core';

/**
 * Configuration for file uploads
 */
export interface UploadConfig {
  /** MIME types string for HTML input accept attribute (e.g., "image/png,image/jpeg") */
  accept: string;
  /** Array of file extensions for display (e.g., ['png', 'jpg']) */
  extensions: string[];
  /** Maximum file size in bytes */
  maxSize: number;
  /** Maximum file size in MB (for display) */
  maxSizeMB: number;
}

/**
 * Centralized service for file upload configuration
 * Ensures consistency across all upload components
 */
@Injectable({
  providedIn: 'root',
})
export class UploadConfigService {
  /**
   * Configuration for trip banner/thumbnail images
   * Accepted formats: JPG, JPEG, PNG
   * Max size: 20MB
   */
  readonly TRIP_IMAGE: UploadConfig = {
    accept: 'image/jpeg,image/png,image/jpg',
    extensions: ['jpg', 'jpeg', 'png'],
    maxSize: 20971520, // 20MB in bytes
    maxSizeMB: 20,
  };

  /**
   * Configuration for company / company client logos
   * Accepted formats: JPG, JPEG, PNG
   * Max size: 5MB (logos are small)
   */
  readonly LOGO: UploadConfig = {
    accept: 'image/jpeg,image/png,image/jpg',
    extensions: ['jpg', 'jpeg', 'png'],
    maxSize: 5242880, // 5MB in bytes
    maxSizeMB: 5,
  };

  /**
   * Configuration for AI document extraction
   * Accepted formats: PDF, DOCX, TXT, PNG, JPEG
   * Max size: 50MB
   *
   * Supports:
   * - PDF documents (with extractable text or scanned/images)
   * - Word documents (.docx)
   * - Plain text files (.txt)
   * - Images (PNG, JPEG) - screenshots or scans
   */
  readonly AI_DOCUMENT: UploadConfig = {
    accept: '.pdf,.docx,.txt,.png,.jpg,.jpeg,.doc',
    extensions: ['pdf', 'docx', 'txt', 'png', 'jpg', 'jpeg', 'doc'],
    maxSize: 52428800, // 50MB in bytes
    maxSizeMB: 50,
  };

  /**
   * Validates if a file matches the given configuration
   * @param file - The file to validate
   * @param config - The upload configuration to validate against
   * @returns Object with isValid flag and optional error message
   */
  validateFile(
    file: File,
    config: UploadConfig,
  ): { isValid: boolean; error?: string } {
    // Check file size
    if (file.size > config.maxSize) {
      return {
        isValid: false,
        error: `File size exceeds ${config.maxSizeMB}MB limit`,
      };
    }

    // Check file extension
    const fileName = file.name.toLowerCase();
    const hasValidExtension = config.extensions.some((ext) =>
      fileName.endsWith(`.${ext}`),
    );

    if (!hasValidExtension) {
      return {
        isValid: false,
        error: `Invalid file format. Accepted formats: ${config.extensions.join(', ')}`,
      };
    }

    return { isValid: true };
  }

  /**
   * Formats file size in human-readable format
   * @param bytes - File size in bytes
   * @returns Formatted string (e.g., "2.5 MB")
   */
  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';

    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  }
}
